// Phase G3 Stage 1a: Pal acceptance gallery.
//
// Renders every Pal color × mood × stage so reviewers can confirm
// §G3-Q3 (pencilwise mascot data → storybook Pal visual) works
// without touching the kid loop. Available at /qa-pal; bypasses
// the gate ladder via App.jsx short-circuit (same pattern as
// /qa-gallery).

import { createFileRoute } from "@tanstack/react-router";
import { Pal } from "@/components/Pal";
import { SubjectPill, ProgressDots } from "@/components/SubjectPill";
import { OfflineBanner } from "@/components/OfflineBanner";
import { CorrectBurst } from "@/components/CorrectBurst";
import { HighFiveCelebration } from "@/components/HighFiveCelebration";
import {
  PENCILWISE_MASCOT_TO_PAL_COLOR,
  palColorFromMascotId,
} from "@/lib/pal";

const COLORS = ["yellow", "blue", "purple", "orange", "green", "pink"] as const;
const MOODS = ["happy", "wave", "listen", "cheer", "calm"] as const;
const STAGES = [0, 1, 2, 3, 4] as const;

function Section({
  title,
  testid,
  children,
}: {
  title: string;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="rounded-xl border border-border bg-card p-6 shadow-soft"
      data-testid={testid}
    >
      <h2 className="font-serif text-2xl mb-4 text-foreground">{title}</h2>
      <div className="flex flex-wrap items-end gap-6">{children}</div>
    </section>
  );
}

function QAPalRoute() {
  // R-1: belt-and-braces — even if a future router change reaches this
  // component outside the App.jsx DEV-gated bypass, never render the raw QA
  // surface in production.
  if (!import.meta.env.DEV) {
    return (
      <div className="sb-root flex min-h-[100dvh] items-center justify-center bg-background p-8 text-center">
        <p className="text-base text-muted-foreground">This page isn't available.</p>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-background font-sans text-foreground p-8">
      <header className="mb-6" data-testid="qa-pal-header">
        <h1 className="font-serif text-4xl text-foreground">
          QA Pal — Phase G3 Stage 1a foundation
        </h1>
        <p className="mt-2 text-muted-foreground">
          Pal (§G3-Q3 mascot adapter) + supporting dependency-free
          components copied from storybook. Engine-hook mocks live at
          src/lib/engine/ awaiting Stage 1b screen ports.
        </p>
      </header>

      <div className="grid gap-6 max-w-5xl">
        <Section title="Pal — 6 colors × happy mood × stage 0" testid="qa-pal-colors">
          {COLORS.map((c) => (
            <div key={c} className="flex flex-col items-center gap-2">
              <Pal color={c} size={72} />
              <div className="text-xs">{c}</div>
            </div>
          ))}
        </Section>

        <Section title="Pal — 5 moods (blue, stage 0)" testid="qa-pal-moods">
          {MOODS.map((m) => (
            <div key={m} className="flex flex-col items-center gap-2">
              <Pal color="blue" mood={m} size={72} />
              <div className="text-xs">{m}</div>
            </div>
          ))}
        </Section>

        <Section title="Pal — 5 evolution stages (terracotta-ish purple)" testid="qa-pal-stages">
          {STAGES.map((s) => (
            <div key={s} className="flex flex-col items-center gap-2">
              <Pal color="purple" stage={s} size={72} />
              <div className="text-xs">stage {s}</div>
            </div>
          ))}
        </Section>

        <Section title="Pal adapter — pencilwise mascot IDs" testid="qa-pal-adapter">
          {Object.entries(PENCILWISE_MASCOT_TO_PAL_COLOR).map(([id, color]) => (
            <div key={id} className="flex flex-col items-center gap-2">
              <Pal color={palColorFromMascotId(id)} size={64} />
              <div className="text-xs">
                <strong>{id}</strong> → {color}
              </div>
            </div>
          ))}
          <div className="flex flex-col items-center gap-2">
            <Pal color={palColorFromMascotId("unknown")} size={64} />
            <div className="text-xs">
              <strong>unknown</strong> → blue (fallback)
            </div>
          </div>
        </Section>

        <Section title="Subject pill + progress dots" testid="qa-subject-pill">
          <SubjectPill subject="MATH" />
          <SubjectPill subject="READING" />
          <SubjectPill subject="WRITING" />
          <ProgressDots done={2} total={5} />
        </Section>

        <Section title="Offline banner" testid="qa-offline-banner">
          <OfflineBanner />
        </Section>

        <Section title="Correct burst" testid="qa-correct-burst">
          <CorrectBurst />
        </Section>

        <Section title="High-five celebration (large)" testid="qa-high-five">
          <div className="relative h-48 w-full max-w-md border border-dashed border-border rounded-md">
            <HighFiveCelebration
              palColor="orange"
              palName="Zest"
              kidName="Sasha"
              onDone={() => {}}
            />
          </div>
        </Section>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/qa-pal")({
  component: QAPalRoute,
});
